/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import { Typography, makeStyles, Box } from "@material-ui/core";
import _ from "lodash";
import { useSelector } from "react-redux";
import { ScaleLoader } from "react-spinners";
import { primaryColor } from "resources/constants";
import BadRequestDialog from "app/common/dialog/BadRequestDialog";
import athenaClient from "lib/axios/clients";
import buildRequestUrl from "lib/requests/requestUtils";
import CompetitorScenarioCardComponent from "./competitors_scenario_component/CompetitorScenarioCardComponent";
import { getAsyncBasicCompetitorsData } from "./utils/getAsyncCompetitorsScenariosBaseData";
import getAsyncCompetitorsScenariosData from "./utils/getAsyncCompetitorsScenariosData";
import getAsyncCompetitorsSlidersData from "./utils/getAsyncCompetitorsSlidersData";

const useStyles = makeStyles(() => ({
  cardMainContainer: {
    margin: "20px 30px 20px 30px",
    border: "2px dotted #a6a6a6",
    borderRadius: "5px",
    backgroundColor: "#fafafa",
    display: "grid",
    alignItems: "center",
    height: "160px",
    "&:hover": {
      boxShadow: "0px 5px 5px -3px rgba(0,0,0,0.1)",
    },
  },
  emptyScenarioContainer: {
    textAlign: "center",
    padding: "40px 0",
    fontWeight: "bold",
    userSelect: "none",
  },
}));
export default function CustomCompetitorsListComponent({
  baseCompetitorScenarioData,
  setBaseCompetitorKpis,
  mainScenarioId,
}) {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(true);
  const [isBadRequest, setIsBadRequest] = useState(false);
  const [competitorsData, setCompetitorsData] = useState([]);

  const competitorsSlice = useSelector((state) => state.catalog.competitors);
  const categoriesSlice = useSelector((state) => state.catalog.categories);
  const companiesSlice = useSelector((state) => state.companies);
  const brandsSlice = useSelector((state) => state.catalog.brands);
  const authSlice = useSelector((state) => state.auth);
  const selectedScenarioSlice = useSelector(
    (state) => state.scenarios.selectedScenario
  );
  const cyclesListSlice = useSelector((state) => state.cycle.type);

  useEffect(() => {
    const getMountComponentData = async () => {
      if (!baseCompetitorScenarioData) return;
      try {
        const competitorsScenariosBaseData = await getAsyncBasicCompetitorsData(
          mainScenarioId
        );

        const competitorScenariosData = await getAsyncCompetitorsScenariosData(
          competitorsScenariosBaseData,
          cyclesListSlice,
          baseCompetitorScenarioData,
          authSlice.user?.username
        );
        if (competitorScenariosData[0]) {
          setBaseCompetitorKpis(competitorScenariosData[0]);
        }

        const resultForHistoricalVolumeByCompany = await athenaClient({
          method: "GET",
          url: buildRequestUrl(
            {
              group_by: "company_id,category_id",
              sum: "units",
              order_by: "sum_units",
              order_direction: "desc",
            },
            `historical_volume_by_company`
          ),
        });

        const resultForHistoricalVolumeByBrand = await athenaClient({
          method: "GET",
          url: buildRequestUrl(
            {
              group_by: "company_id,category_id,brand_id",
              sum: "units",
              order_by: "sum_units",
              order_direction: "desc",
            },
            `historical_volume_by_company`
          ),
        });

        const resultForCompetitorScenarioByCycle = await athenaClient({
          method: "GET",
          url: buildRequestUrl(
            {
              scenario_type_id: "4",
              cycle_id: baseCompetitorScenarioData?.cycleId,
            },
            `scenarios`
          ),
        });

        const resultForCompetitorScenarioEstimations = await athenaClient({
          method: "GET",
          url: buildRequestUrl(
            {
              // We get the first element of data because in competitors
              // only exists one scenario per cycle
              scenario_id: resultForCompetitorScenarioByCycle.data[0].id,
              expand: "product",
            },
            `scenario_estimations`
          ),
        });
        const competitorsSlidersPromises = [];
        _.times(3, (idx) => {
          competitorsSlidersPromises.push(
            getAsyncCompetitorsSlidersData(
              competitorScenariosData[idx],
              resultForHistoricalVolumeByCompany.data,
              resultForCompetitorScenarioEstimations.data,
              selectedScenarioSlice,
              competitorsSlice,
              categoriesSlice,
              companiesSlice,
              brandsSlice,
              resultForHistoricalVolumeByBrand.data
            )
          );
        });
        const competitorsSlidersResponses = await Promise.all(
          competitorsSlidersPromises
        );

        const newCompetitorsSlidersData = [];
        _.times(3, (idx) => {
          const scenarioData =
            competitorScenariosData[idx] !== undefined
              ? {
                  ...competitorScenariosData[idx],
                  sliderResponse: competitorScenariosData[idx].sliderData,
                }
              : null;
          newCompetitorsSlidersData.push({
            scenarioData,
            slidersData: competitorsSlidersResponses[idx],
            id: idx,
          });
        });
        setCompetitorsData(newCompetitorsSlidersData);

        setIsLoading(false);
      } catch (error) {
        setIsBadRequest(true);
      }
    };
    setIsLoading(true);
    getMountComponentData();
  }, [baseCompetitorScenarioData]);

  let competitorsElements = _.times(3, (i) => (
    <Box className={classes.cardMainContainer} key={i}>
      <Box className={classes.emptyScenarioContainer}>
        <ScaleLoader
          size={150}
          color={primaryColor}
          loading
          data-testid="spinner"
          className={classes.messageIcon}
        />
      </Box>
    </Box>
  ));

  if (!isLoading) {
    if (baseCompetitorScenarioData?.scenarioId) {
      competitorsElements = _.times(3, (i) => (
        <CompetitorScenarioCardComponent
          baseCompetitorScenarioData={baseCompetitorScenarioData}
          competitorData={competitorsData[i]}
          setCompetitorsData={setCompetitorsData}
          setBaseCompetitorKpis={setBaseCompetitorKpis}
        />
      ));
    } else {
      competitorsElements = _.times(3, (i) => (
        <Box className={classes.cardMainContainer} key={i}>
          <Box className={classes.emptyScenarioContainer}>
            <Typography variant="body1" className={classes.titleCard}>
              Select one base scenario to see competitor data.
            </Typography>
          </Box>
        </Box>
      ));
    }
  }
  return (
    <>
      <BadRequestDialog open={isBadRequest} />
      {competitorsElements}
    </>
  );
}
